import { useController } from 'react-hook-form'
import { useMemo, useCallback, useRef, useState } from 'react'
import moment from 'moment'
import {
  computeFuelSurchargeDollarAmount,
  computeFuelSurchargePercent,
} from '../../../components/OrderForm/orderCalculations.js'

/**
 * Empty value for numeric (function-shaped) masks — `null`, never `undefined`.
 *
 * RHF's `useWatch` freezes the field's mount-time value in a ref and its `get()`
 * falls back to it whenever the stored value is `undefined`, so writing
 * `undefined` on clear makes the input snap back to the value it loaded with
 * (edit-mode price fields mount after `form.reset`, so they always hit this).
 * `null` is a defined value, so it propagates and the clear sticks. The order
 * and lane price schemas already normalize `null` to `undefined`.
 * @private
 */
const EMPTY_MASKED_NUMBER = null

/**
 * Format number with thousand separators
 * @param {number|string} value - Raw number value
 * @returns {string} Formatted string (e.g., "45,000")
 * @private
 */
const formatNumber = (value) => {
  if (!value && value !== 0) return ''
  const num = parseFloat(value.toString().replace(/,/g, ''))
  if (isNaN(num)) return value
  return num.toLocaleString('en-US')
}

/**
 * Parse formatted number string to raw number
 * @param {string} value - Formatted string (e.g., "45,000")
 * @returns {number} Raw number value
 * @private
 */
const parseNumber = (value) => {
  if (!value && value !== 0) return ''
  const parsed = parseFloat(value.toString().replace(/,/g, ''))
  return isNaN(parsed) ? '' : parsed
}

/**
 * Format a stored number as a padded-cents amount for a currency-masked field
 * Opt-in via displayFormat='currency': the mask itself renders whatever digits
 * it is given, so 1.5 shows as $1.5 unless the cents are padded here. Only the
 * blurred display goes through this — while the user types, the draft wins.
 * @param {number|string} value - Stored value
 * @returns {string} Display string ('' when there is no value)
 * @private
 */
const formatCents = (value) => {
  if (value === '' || value === null || value === undefined) return ''
  const num = parseFloat(value.toString().replace(/,/g, ''))
  return isNaN(num) ? '' : num.toFixed(2)
}

const DATE_DISPLAY_FORMAT = 'MM/DD/YYYY'

/**
 * Format a stored date value for an MM/DD/YYYY masked input
 * Server values arrive as ISO strings; anything shorter than a full date is a
 * partial the user is mid-way through typing and passes through untouched.
 * @param {string} value - Stored date value (ISO or MM/DD/YYYY)
 * @returns {string} Display string
 * @private
 */
const formatDate = (value) => {
  if (!value) return ''
  const text = value.toString()
  return text.length >= DATE_DISPLAY_FORMAT.length
    ? moment(text, [DATE_DISPLAY_FORMAT, moment.ISO_8601]).format(
        DATE_DISPLAY_FORMAT,
      )
    : text
}

/**
 * Sanitize masked input value for storage
 * Removes mask characters and returns only the raw data
 * @param {string} value - Masked string (e.g., "$25.50", "(123) 456-7890")
 * @param {boolean} hasMask - Whether this field uses a mask
 * @returns {string} Sanitized string (digits and decimal point only)
 * @private
 */
const sanitizeMaskedValue = (value, hasMask) => {
  if (!hasMask || !value) return value

  // Keep digits and decimal point so function-shaped numeric masks
  // (currency, percent) parse fractional values correctly. Phone/zip
  // masks contain no `.` so behavior there is unchanged.
  return value.replace(/[^\d.]/g, '')
}

/**
 * Parse a sanitized numeric-mask string to a number
 * Partial input ('1.', '.') must never reach form state as a string — the order
 * and lane price schemas expect a number, and a string fails their union with
 * "Expected number, received string".
 * @param {string} sanitized - Digits and decimal point only
 * @returns {number|null} Parsed number, or the empty value when unparseable
 * @private
 */
const parseMaskedNumber = (sanitized) => {
  const num = parseFloat(sanitized)
  return isNaN(num) ? EMPTY_MASKED_NUMBER : num
}

/**
 * FormInput Logic Hook
 * Handles React Hook Form integration for text input fields
 *
 * @param {Object} props
 * @param {string} props.name - Field name for RHF
 * @param {Object} props.control - RHF control object
 * @param {string} [props.defaultValue=''] - Default value
 * @param {Object} [props.rules] - RHF validation rules
 * @param {string} [props.type='text'] - Input type (text, number, email, etc.)
 * @param {string} [props.displayFormat] - Optional display formatting ('number' for thousand separators, 'currency' for padded cents on a currency-masked field, 'date' for MM/DD/YYYY over an ISO value)
 * @param {string} [props.displayMode] - Display mode for dual-mode fields ('currency' | 'percent')
 * @param {Object} [props.conversionContext] - Context for display mode conversions (e.g., { lineHaulPrice })
 * @param {Array|Function} [props.mask] - Input mask (e.g., phone mask)
 * @param {string} [props.maskPlaceholder] - Placeholder for masked input
 * @param {node} [props.prefix] - Content to display before input (wrapped in span)
 * @param {node} [props.suffix] - Content to display after input (wrapped in span)
 * @returns {Object} Field state and handlers
 */
export const useFormInput = ({
  name,
  control,
  defaultValue = undefined,
  rules,
  type = 'text',
  displayFormat,
  displayMode,
  conversionContext = {},
  mask,
  maskPlaceholder,
  prefix,
  suffix,
}) => {
  const {
    field,
    fieldState: { error, invalid },
  } = useController({
    name,
    control,
    defaultValue,
    rules,
  })

  // createNumberMask returns a function; phone/zip/date masks are arrays
  const isNumericMask = typeof mask === 'function'
  const isDateFormat = displayFormat === 'date'

  // While the user types in a numeric-masked field the displayed text must come
  // from what they typed, not from the parsed number: feeding the number back
  // makes react-text-mask re-conform the DOM (String(1) for '1.0'), which eats
  // the decimal mid-keystroke and turns $1.05 into $15. The draft is dropped on
  // blur (so the display re-canonicalizes from the stored number) and ignored as
  // soon as the stored value changes from anywhere other than our own last write.
  //
  // Date fields keep a draft for the second reason only: the stored value may be
  // an ISO string that has to render as MM/DD/YYYY, so the display can't just be
  // the stored value, and re-formatting mid-entry would rewrite what the user is
  // typing. Their draft is NOT dropped on blur — formatDate over a half-typed or
  // impossible date returns 'Invalid date', which would replace the user's text.
  const keepsDraft = isNumericMask || isDateFormat
  const [draft, setDraft] = useState(null)
  const lastWrittenRef = useRef(undefined)
  const draftIsCurrent =
    draft !== null && field.value === lastWrittenRef.current

  // Format display value for number type or displayMode conversion
  const displayValue = useMemo(() => {
    if (draftIsCurrent) {
      return draft
    }

    // MM/DD/YYYY display over a stored ISO string. Running on every render (not
    // seeded once into local state) is what makes an external reset — Cancel, a
    // refetch, a post-save reset — re-render the stored value instead of leaving
    // the abandoned draft on screen while the form reads clean.
    if (isDateFormat) {
      return formatDate(field.value)
    }

    // Handle displayMode conversion (e.g., $ ↔ % for fuel surcharge)
    if (displayMode === 'percent' && conversionContext.lineHaulPrice) {
      // Convert stored dollar amount to percentage for display
      const percent = computeFuelSurchargePercent(
        conversionContext.lineHaulPrice,
        field.value,
      )
      return percent || ''
    }

    // Handle number formatting (thousand separators)
    if (displayFormat === 'number' && field.value) {
      return formatNumber(field.value)
    }

    // Padded cents for a currency-masked field once the draft is gone
    if (displayFormat === 'currency') {
      return formatCents(field.value)
    }

    return field.value ?? ''
  }, [
    field.value,
    displayFormat,
    displayMode,
    conversionContext,
    draft,
    draftIsCurrent,
    isDateFormat,
  ])

  // Handle onChange with format parsing and mask sanitization
  const handleChange = useCallback(
    (e) => {
      // Get value from event (works for both regular inputs and MaskedInput)
      const value = e.target?.value ?? e.value ?? ''
      let parsedValue = value

      // Handle displayMode conversion (e.g., % → $ for storage)
      if (displayMode === 'percent' && conversionContext.lineHaulPrice) {
        // Convert percent input to dollar amount for storage
        if (value === '' || value === undefined || value === null) {
          parsedValue = EMPTY_MASKED_NUMBER
        } else {
          // Remove % suffix if present (from mask)
          const percentValue = parseFloat(value.toString().replace('%', ''))
          if (!isNaN(percentValue)) {
            parsedValue = computeFuelSurchargeDollarAmount(
              conversionContext.lineHaulPrice,
              percentValue,
            )
          } else {
            parsedValue = EMPTY_MASKED_NUMBER
          }
        }
      }
      // Dates keep their mask separators: the stored value IS the MM/DD/YYYY
      // string, and '' means unset — the schemas type these as strings, so the
      // digits-only sanitize below would corrupt them and undefined would fail.
      else if (isDateFormat) {
        parsedValue = value
      }
      // Parse formatted number back to raw number
      else if (displayFormat === 'number') {
        parsedValue = parseNumber(value)
      }
      // Convert to number for type="number" inputs (without displayFormat)
      else if (type === 'number' && value !== '') {
        parsedValue = parseFloat(value)
        // If parsing fails, store empty string instead of NaN
        if (isNaN(parsedValue)) {
          parsedValue = ''
        }
      }
      // Sanitize masked input (remove mask characters)
      else if (mask) {
        const sanitized = sanitizeMaskedValue(value, true)

        if (isNumericMask) {
          // Always a number or the empty value — never a partial string
          parsedValue =
            sanitized === ''
              ? EMPTY_MASKED_NUMBER
              : parseMaskedNumber(sanitized)
        } else {
          // Phone/zip keep the digits-only string; empty stays undefined
          parsedValue = sanitized === '' ? undefined : sanitized
        }
      }

      // Keep the typed text on screen, and record what we wrote so an external
      // change to the field invalidates the draft
      if (keepsDraft) {
        setDraft(value)
        lastWrittenRef.current = parsedValue
      }

      field.onChange(parsedValue)
    },
    [
      field,
      type,
      displayFormat,
      displayMode,
      conversionContext,
      mask,
      isNumericMask,
      isDateFormat,
      keepsDraft,
    ],
  )

  // Drop the draft so the display re-formats from the stored number on blur
  const handleBlur = useCallback(() => {
    if (isNumericMask) {
      setDraft(null)
    }
    field.onBlur()
  }, [field, isNumericMask])

  return {
    // Field props from RHF
    value: displayValue,
    onChange: handleChange,
    onBlur: handleBlur,
    name: field.name,
    ref: field.ref,

    // Validation state
    error: invalid,
    errorMessage:
      error?.message || (invalid ? 'This field is required' : undefined),

    // Mask props
    mask,
    maskPlaceholder,

    // Prefix/suffix props (for children pattern)
    prefix,
    suffix,
  }
}
